import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { KeyRound, ArrowLeft, MailCheck } from 'lucide-react';
import { forgotPassword } from '../api/auth';

export const ForgotPasswordPage: React.FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await forgotPassword(identifier.trim());
      setMessage(res.message);
      setSent(true);
    } catch {
      // Same neutral message either way — never leak account existence
      setMessage('If that account exists and has an email, a reset link has been sent.');
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'var(--bg)' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-sm glass-strong rounded-3xl p-10"
        style={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.04) inset, 0 40px 80px rgba(0,0,0,0.6)' }}
      >
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-full bg-gold-glow border border-gold-dim flex items-center justify-center mx-auto mb-4">
            {sent ? <MailCheck size={22} className="text-gold" /> : <KeyRound size={22} className="text-gold" />}
          </div>
          <h1 className="font-display text-xl font-bold text-cream mb-1">
            {sent ? 'Check your email' : 'Forgot your password?'}
          </h1>
          <p className="text-xs text-text-dim leading-relaxed">
            {sent
              ? message
              : 'Enter your username or email and we will send you a reset link.'}
          </p>
        </div>

        {!sent && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-text-dim mb-2">
                Username or Email
              </label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                autoFocus
                className="w-full bg-white/[0.04] border-0 border-b-[1.5px] border-white/10 px-0.5 py-2.5 text-cream text-sm outline-none transition-colors duration-200 focus:border-gold placeholder:text-text-dim"
                placeholder="Enter username or email"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !identifier.trim()}
              className="w-full py-3 btn-gold rounded-xl text-[13px] font-semibold tracking-wide disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}

        {sent && (
          <p className="text-[11px] text-text-dim text-center leading-relaxed mb-2">
            No email on your account? Ask your admin to issue a reset link from the admin panel.
          </p>
        )}

        <p className="text-center mt-6">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-gold-text hover:text-gold-light font-medium transition-colors">
            <ArrowLeft size={13} />
            Back to sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};
